'use client'

import React, { useState, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {
  Search,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import type { VisaType } from '@/payload-types'
import { useSearch } from '../../hooks/useSearch'
import { mediaFile } from '../../lib/media'

const VISA_CATEGORIES = [
  { label: 'Work Visa', value: 'work' },
  { label: 'Family Visa', value: 'family' },
  { label: 'Student Visa', value: 'student' },
  { label: 'Tourist Visa', value: 'tourist' },
  { label: 'Investor Visa', value: 'investor' },
  { label: 'Green Card', value: 'green-card' },
  { label: 'Other', value: 'other' },
] as const

const SORT_OPTIONS = [
  { id: 'name-asc', label: 'Name (A-Z)' },
  { id: 'name-desc', label: 'Name (Z-A)' },
]

interface VisaListHeroProps {
  visaTypes: VisaType[]
  totalCount: number
  initialCategories?: string[]
}

const VALID_CATEGORIES = new Set<string>(VISA_CATEGORIES.map((category) => category.value))

export function VisaListHero({
  visaTypes,
  totalCount: _totalCount,
  initialCategories = [],
}: VisaListHeroProps) {
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    matchingIds,
  } = useSearch({ collection: 'visa-types' })
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() =>
    initialCategories.filter((category) => VALID_CATEGORIES.has(category)),
  )
  const [sortBy, setSortBy] = useState('name-asc')
  const [sortOpen, setSortOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 100

  // Filter and sort visa types
  const filteredVisaTypes = useMemo(() => {
    let filtered = visaTypes

    // Search filter — when matchingIds is null (no query), show all
    if (matchingIds !== null) {
      filtered = filtered.filter((visa) => matchingIds.has(visa.id))
    }

    // Filter by selected categories (using Payload category field)
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((visa) => {
        const visaCategories = visa.category || []
        return selectedCategories.some((cat) => visaCategories.includes(cat as any))
      })
    }

    // Sort
    switch (sortBy) {
      case 'name-asc':
        filtered = [...filtered].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        break
      case 'name-desc':
        filtered = [...filtered].sort((a, b) => (b.name || '').localeCompare(a.name || ''))
        break
    }

    return filtered
  }, [visaTypes, matchingIds, selectedCategories, sortBy])

  // Pagination
  const totalPages = Math.ceil(filteredVisaTypes.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedVisaTypes = filteredVisaTypes.slice(startIndex, startIndex + itemsPerPage)

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId) ? prev.filter((c) => c !== categoryId) : [...prev, categoryId],
    )
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1) // Reset to first page when searching
  }

  const getSortLabel = () => {
    return SORT_OPTIONS.find((opt) => opt.id === sortBy)?.label || 'Name (A-Z)'
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages)
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
      }
    }
    return pages
  }

  return (
    <>
      <style jsx global>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <section className="relative text-white pt-6">
        {/* Background Image Section */}
        <div className="relative flex items-center min-h-[50vh]">
          {/* Background Image */}
          <div className="absolute inset-0 bg-[#24253a]">
            <Image
              src={mediaFile('visas-hero.png')}
              alt="Visa paths background"
              fill
              sizes="100vw"
              className="object-cover object-center"
              priority
            />
          </div>
          {/* Overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />

          <div className="relative z-10 max-w-380 mx-auto px-6 md:px-12 py-24 w-full">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-5xl font-medium leading-tight mt-12 mb-8">
                Find{' '}
                <span className="font-serif italic underline decoration-2 underline-offset-4">
                  your
                </span>{' '}
                visa path.
              </h1>
              <p className="text-white/90 text-base md:text-lg leading-relaxed mb-0 2xl:mb-12 max-w-2xl">
                Explore our comprehensive guide to U.S. visa categories. Each visa type has specific
                requirements and benefits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Search Bar & Filters - straddles hero/grid boundary */}
      <div className="relative z-20 -mt-8 mb-8 w-full">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {/* AI Search Bar */}
          <form onSubmit={handleSearch} className="relative mb-6">
            <div className="relative bg-white rounded-full overflow-hidden shadow-lg">
              <div className="flex items-center px-4 sm:px-6 py-3 sm:py-4">
                <Search className="w-5 h-5 text-gray-500 mr-3 shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setCurrentPage(1)
                  }}
                  placeholder="What are you looking for today?"
                  className="flex-1 outline-none text-gray-900 placeholder:text-gray-500 text-base bg-transparent"
                />
                <button
                  type="submit"
                  className="ml-3 sm:ml-4 w-10 h-10 rounded-full bg-brand-blue hover:bg-brand-blue/90 transition-colors flex items-center justify-center shrink-0"
                  aria-label="Search"
                >
                  <ArrowRight className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Category Filter Pills - full width scroll on mobile */}
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-3 items-center sm:justify-center text-sm px-6 sm:px-0 sm:flex-wrap w-max sm:w-full sm:max-w-5xl sm:mx-auto pb-2 sm:pb-0">
            {VISA_CATEGORIES.map((category) => (
              <button
                key={category.value}
                onClick={() => toggleCategory(category.value)}
                className={`px-5 py-2 rounded-full border transition-colors whitespace-nowrap shrink-0 ${
                  selectedCategories.includes(category.value)
                    ? 'bg-brand-blue text-white border-brand-blue'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-brand-blue hover:bg-gray-50'
                }`}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Visa Cards Grid Section */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-380 mx-auto">
          {/* Sort Controls */}
          <div className="flex items-center justify-end mb-8">
            <div className="flex items-center gap-2">
              <span className="text-gray-600 text-sm">Sort By:</span>
              <div className="relative">
                <button
                  onClick={() => setSortOpen(!sortOpen)}
                  className="px-4 py-1.5 rounded-full bg-white hover:bg-gray-50 transition-colors border border-gray-300 flex items-center gap-2 min-w-35 justify-between text-sm"
                >
                  <span>{getSortLabel()}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                {sortOpen && (
                  <div className="absolute top-full mt-1 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-40">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setSortBy(option.id)
                          setSortOpen(false)
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-100 transition-colors first:rounded-t-lg last:rounded-b-lg text-sm ${
                          sortBy === option.id ? 'bg-gray-50 font-medium' : ''
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Visa Cards Grid */}
          {paginatedVisaTypes.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedVisaTypes.map((visa) => (
                <Link
                  key={visa.id}
                  href={`/visas/${visa.slug}`}
                  className="bg-white rounded-2xl p-6 md:p-8 border border-primary/30 hover:border-brand-blue transition-colors group flex flex-col"
                >
                  <h3 className="text-xl md:text-2xl font-medium text-brand-gold mb-3">
                    {visa.name}
                  </h3>
                  <p className="text-sm md:text-base text-gray-700 mb-4 grow line-clamp-3">
                    {visa.shortDescription}
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm md:text-base font-semibold text-primary transition-colors">
                    Learn More
                    <ArrowUpRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600">No visa types match your search criteria.</p>
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCategories([])
                  setCurrentPage(1)
                }}
                className="mt-4 text-brand-blue hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-2 mt-12">
              {/* Results count */}
              <span className="text-sm text-gray-600 sm:mr-4">
                Showing {startIndex + 1}-
                {Math.min(startIndex + itemsPerPage, filteredVisaTypes.length)} of{' '}
                {filteredVisaTypes.length} results
              </span>

              <div className="flex items-center gap-2">
                {/* Previous button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page numbers */}
                {getPageNumbers().map((page, index) => (
                  <React.Fragment key={index}>
                    {page === '...' ? (
                      <span className="w-8 h-8 flex items-center justify-center text-gray-500">
                        ···
                      </span>
                    ) : (
                      <button
                        onClick={() => setCurrentPage(page as number)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm transition-colors ${
                          currentPage === page
                            ? 'bg-brand-blue text-white'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    )}
                  </React.Fragment>
                ))}

                {/* Next button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
